import {Component, OnInit, Input} from '@angular/core';
import {
  ApplicantIdentityResponseRequest,
  ApplicantRequest, ApplicationLogDetailsRequest,
  ApplicationLogsRequest, ApplicationLogStepRequest,
  ApplicationLogsUpdateRequest,
  Log,
  Step
} from "leia-api";
import {FacadeService} from "../../service-facade/facade.service";
import {UtilitiesService} from "../../services/utilities.service";
import {ProcessService} from "../../../process/process.service";
import {CoreService} from "../../services/core.service";
import {EvaluateRequest} from "skyhopper-api";
import {AlloyResult} from "../../model/alloy-result";
import {ReferenceCode} from "../../../process/models/referenceCode";
import {AlloyOutcome} from "../../model/alloy-outcome";
import {environment} from "../../../../environments/environment";
import {AlloyMode} from "./alloy-mode";
import {ERROR_PATH} from "../../constants";
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import StatusEnum = ApplicationLogsUpdateRequest.StatusEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import ServiceTypeEnum = ApplicantIdentityResponseRequest.ServiceTypeEnum;

@Component({
  selector: 'app-alloy-evaluate',
  templateUrl: './alloy-evaluate.component.html',
  styleUrls: ['./alloy-evaluate.component.css']
})
export class AlloyEvaluateComponent implements OnInit {

  @Input() isAlloyEvaluateRequired: boolean = false;

  alloyMode : string = AlloyMode.EVAL;
  step : NameEnum = NameEnum.AlloyKyc;
  onboardingStepEnum : OnboardingStepEnum = OnboardingStepEnum.AlloyKyc;
  serviceType : ServiceTypeEnum = ServiceTypeEnum.AlloyKyc;

  applicantRequests: ApplicantRequest[] = new Array();

  applicantIdentityResponseIdMap: Map<number, number> = new Map<number, number>();

  alloyRetryAttempted : boolean = false;

  constructor(private facadeService: FacadeService,
              private utilitiesService: UtilitiesService,
              private processService: ProcessService,
              private coreService: CoreService) {
  }

  ngOnInit(): void {
    this.facadeService.getShoppingCartOnboardingRequest().applicants?.forEach(applicant => {
      this.facadeService.getShoppingCartApplicationLogsRequest().applicants?.forEach(applicantRequest => {
        // Bypassed applicants (isAlloyEvaluateRequired=false) still need to be queued for document verification.
        if(this.isAlloyEvaluateRequired) {
          if(applicant.customerInfo.isAlloyEvaluateRequired && applicantRequest.applicantId === applicant.applicantId) {
             this.applicantRequests.push(applicantRequest);
          }
          return;
        }
        if(applicantRequest.applicantId === applicant.applicantId) this.applicantRequests.push(applicantRequest);
      });

      // Only applicants that still require an Alloy Evaluate call (and were not bypassed) need to be
      // pre-registered here. Mode (EVAL vs DOCV) is determined per-applicant in executeAlloyCall() below -
      // it must NOT be derived here from the last applicant seen in this loop, since a bypassed co-applicant
      // already has an entity token before this component even runs and would otherwise flip the mode
      // for every other applicant (including ones still needing EVAL).
      if (applicant.customerInfo.isAlloyEvaluateRequired && !this.processService.alloyByPassEvaluationMap.has(applicant.applicantId!)) {
        let applicantIdentityResponseRequest: ApplicantIdentityResponseRequest = new ApplicantIdentityResponseRequest();

        applicantIdentityResponseRequest.applicantId = applicant.applicantId;
        applicantIdentityResponseRequest.serviceType = ServiceTypeEnum.AlloyKyc;

        this.facadeService.createNewApplicantIdentityResponse(applicantIdentityResponseRequest).subscribe(value => {
          this.applicantIdentityResponseIdMap.set(applicant.applicantId!, value);
        });
      }
    });
    this.executeAlloyCall(this.applicantRequests[0], 0);
  }

  executeAlloyCall(applicantRequest: ApplicantRequest, index: number) {
    // Determine mode for THIS specific applicant only - an applicant already holding an entity token
    // (from a completed Evaluate call or an evaluate bypass) is ready for Document Verification; otherwise
    // they still need Evaluate. Must be re-evaluated on every call since applicants can be in different states.
    if (!this.isAlloyEvaluateRequired && this.processService.alloyEntityTokenMap.has(applicantRequest.applicantId!)) {
      this.step = NameEnum.AlloyDocumentVerification;
      this.onboardingStepEnum = OnboardingStepEnum.AlloyDocumentVerification;
      this.alloyMode = AlloyMode.DOCV;
      this.serviceType = ServiceTypeEnum.AlloyDocV;
    } else {
      this.step = NameEnum.AlloyKyc;
      this.onboardingStepEnum = OnboardingStepEnum.AlloyKyc;
      this.alloyMode = AlloyMode.EVAL;
      this.serviceType = ServiceTypeEnum.AlloyKyc;
    }

    let applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    let evaluateRequest: EvaluateRequest & { applicantId?: number } = {} as EvaluateRequest & { applicantId?: number };

    const sessionId = this.facadeService.getShoppingCartApplicationLogsRequest().sessionId!;

    const {
      firstName,
      lastName,
      ssn,
      phoneNumber,
      applicantId,
      dateOfBirth,
      email
    } = applicantRequest;

    let {middleName, suffix} = applicantRequest;
    let { streetAddress, streetAddress1, city, state, zip } = applicantRequest.addresses![0];

    let existingCustomer = false;
    if (this.processService.alloyExistingCustomerMap.has(applicantId!) && this.processService.alloyExistingCustomerMap.get(applicantId!)) existingCustomer = true;

    middleName = middleName ?? "";
    suffix = suffix ?? "";
    streetAddress1 = streetAddress1 ?? "";

    evaluateRequest.applicantId = applicantId;
    evaluateRequest.sessionId = sessionId;
    evaluateRequest.nameFirst = firstName;
    evaluateRequest.nameMiddle = middleName || "";
    evaluateRequest.nameLast = lastName;
    evaluateRequest.nameSuffix = suffix || "";
    evaluateRequest.phoneNumber = phoneNumber;
    evaluateRequest.documentSSN = ssn;
    evaluateRequest.addressLine1 = streetAddress;
    evaluateRequest.addressLine2 = streetAddress1;
    evaluateRequest.addressCity = city;
    evaluateRequest.addressState = state;
    evaluateRequest.addressPostalCode = zip;
    evaluateRequest.birthDate = dateOfBirth;
    evaluateRequest.emailAddress = email;
    evaluateRequest.addressCountryCode = "US";
    evaluateRequest.socialTwitter = "";
    evaluateRequest.existingCustomer = existingCustomer;
    evaluateRequest.socureSessionId = UtilitiesService.deviceRiskSessionId;

    let documentVerify : boolean = false;

    const alloyDocVWorkflowVersion : string = this.facadeService.getStorageItem("alloyDocVWorkflowVersion");

    let entityTokenId = "";
    let socureDocumentUuid = "";
    let alloyWorkflowVersion = this.facadeService.getStorageItem("alloyWorkflowId");

    if(this.step === NameEnum.AlloyDocumentVerification) {
      documentVerify = true;
      alloyWorkflowVersion = alloyDocVWorkflowVersion;

      if(this.processService.alloyEntityTokenMap.has(applicantId!))
        entityTokenId = this.processService.alloyEntityTokenMap.get(applicantId!)!;

      if(this.processService.socureDocumentUuIdMap.has(applicantRequest.applicantId!)) socureDocumentUuid = this.processService.socureDocumentUuIdMap.get(applicantRequest.applicantId!)!;
    }

    evaluateRequest.documentUuid = socureDocumentUuid;

    if (environment.development) console.warn( this.alloyMode + ' alloyWorkFlowId: ' + alloyWorkflowVersion + ' request: ' + JSON.stringify(evaluateRequest) + ' entityTokenId: ' + entityTokenId);

    this.facadeService.executeAlloyCall(evaluateRequest, documentVerify, alloyWorkflowVersion, entityTokenId).subscribe(async salesForceAlloyObject => {
      if (environment.development) console.warn('salesForceAlloyObject: ' + JSON.stringify(salesForceAlloyObject));

      let logs: Log[] = [];
      let applicationLogDetailsRequests: ApplicationLogDetailsRequest[] = [];

      if (salesForceAlloyObject === undefined || salesForceAlloyObject === null) {
        logs.push(this.utilitiesService.createLog("SKH", "salesForceAlloyObject not returned correctly."));
        logs.push(this.utilitiesService.createLog("BB8", `Alloy ${this.alloyMode} failed.`));

        applicationLogDetailsRequests.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Skyhopper, "salesForceAlloyObject not returned correctly."));
        applicationLogDetailsRequests.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Alloy ${this.alloyMode} failed.`));

        const clonedLogs = Object.assign([], logs);
        const clonedLogDetails = Object.assign([], applicationLogDetailsRequests);

        applicationRequest.rcCode = this.alloyMode === AlloyMode.EVAL? ReferenceCode.AlloyFailure: ReferenceCode.DocVFailure

        applicationRequest.step = this.utilitiesService.createApplicantStep(this.step, applicantRequest.applicantId!, StateEnum.Fail, clonedLogs);
        applicationRequest.applicationLogStepRequest = this.utilitiesService.createEntireApplicationLogStepRequest(this.onboardingStepEnum, OnboardingStateEnum.Fail, OnboardingSourceEnum.Bb8, applicantId!, applicationRequest.rcCode, clonedLogDetails);

        applicationRequest.status = applicationRequest.rcCode === ReferenceCode.AlloyFailure ? StatusEnum.Error : StatusEnum.Rejected;

        await this._updateApplication(applicationRequest);
        this._handleAlloyFailure(this.alloyMode === AlloyMode.EVAL? ReferenceCode.AlloyFailure: ReferenceCode.DocVFailure);
      } else {
        const {result, outcome, tags} = salesForceAlloyObject.summary!;
        const {entity_token, evaluation_token} = (salesForceAlloyObject as any);

        const isQualifileDeclined = tags?.includes('Qualifile Decline');

        const jsonResponse: string = JSON.stringify(salesForceAlloyObject);

        if (environment.development) console.warn('jsonResponse: ' + jsonResponse);

        let applicantType = "Primary Applicant";
        applicationRequest.applicants?.forEach(applicant => {
          if (applicant.applicantId === applicantId) {
            if(applicant.applicantIdentityResponses == undefined)
              applicant.applicantIdentityResponses = new Array<ApplicantIdentityResponseRequest>();

            if(!applicant.primary) applicantType = "Co-Applicant";

            let applicantIdentityResponseRequest: ApplicantIdentityResponseRequest = new ApplicantIdentityResponseRequest();

            applicantIdentityResponseRequest.applicantId = applicantId;
            applicantIdentityResponseRequest.outcome = outcome;
            applicantIdentityResponseRequest.token = evaluation_token;
            applicantIdentityResponseRequest.eventId = entity_token;
            applicantIdentityResponseRequest.serviceType = this.serviceType;

            applicantIdentityResponseRequest.applicantIdentityResponseId = this.applicantIdentityResponseIdMap.get(applicantRequest.applicantId!);

            this.processService.alloyEntityTokenMap.set(applicantId!, entity_token);

            if(this.alloyMode === AlloyMode.EVAL) this.processService.alloyEvaluateOutcomeMap.set(applicantId!, outcome!);
            if(this.alloyMode === AlloyMode.DOCV) this.processService.alloyDocVOutcomeMap.set(applicantId!, outcome!);

            applicant.entityToken = entity_token;
            applicant.applicantIdentityResponses?.push(applicantIdentityResponseRequest);
          }
        });

        logs.push(this.utilitiesService.createLog("BB8", `${firstName} ${lastName} (${applicantType}), Alloy ${this.alloyMode} Complete. Response: ${jsonResponse}.`));
        applicationLogDetailsRequests.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Skyhopper, `${firstName} ${lastName} (${applicantType}), Alloy ${this.alloyMode} Complete. Response: ${jsonResponse}.`));

        let stepState = StateEnum.Success;
        let appState = applicationRequest.status!;
        let rcCode : any = null;

        switch (result) {
          case AlloyResult.Fail: // on fail, update step and app state and nav to error
            stepState = StateEnum.Fail;
            appState = StatusEnum.Rejected;
            break;

          case AlloyResult.Success:  // on success
            switch (outcome) {
              case undefined: // if outcome is undefined
              case AlloyOutcome.Denied: // or outcome is denied
                stepState = StateEnum.Fail; // update step and app states to failed and nav to error
                appState = StatusEnum.Rejected;
                break;
              case AlloyOutcome.ManualReview:
                stepState = StateEnum.Fail;
                break;
              case AlloyOutcome.DocumentResubmit:
                stepState = StateEnum.Fail;
                appState = StatusEnum.UnderReview;
                break;

              case AlloyOutcome.Approved:
                break;

              default: // if we don't recognize the outcome
                stepState = StateEnum.Fail;
                appState = StatusEnum.UnderReview;
                break;
            }

            break;

          default: // on partial or unknown, update step and app state and nav to error
            stepState = StateEnum.Fail;
            appState = StatusEnum.UnderReview;
            break;
        }

        //  If we are in Step 2 - Alloy Doc V and Approved - we need to check for the existence of any
        //  Manual Review Evaluate call - if located and if this is the final Applicant to go thru DocV - then we want to assign
        //  this Application to Under Review and push out the RC Code.
        if(this.alloyMode === AlloyMode.DOCV && outcome === AlloyOutcome.Approved) {
          let manualReviewFound : boolean = false;
          this.processService.alloyEvaluateOutcomeMap.forEach((value, key) => {
            if(value === AlloyOutcome.ManualReview) manualReviewFound = true;
          });

          if(manualReviewFound && this.applicantRequests.length === this.processService.alloyDocVOutcomeMap.size) appState = StatusEnum.UnderReview;
        }

        if(this.alloyMode === AlloyMode.DOCV && outcome === AlloyOutcome.ManualReview) {
          appState = StatusEnum.UnderReview;
          rcCode = ReferenceCode.DocVUnderReview;
        }

        if(this.alloyMode === AlloyMode.EVAL && outcome === AlloyOutcome.ManualReview) rcCode = ReferenceCode.AlloyUnderReview;

        if(this.alloyMode === AlloyMode.EVAL && outcome === AlloyOutcome.Denied) rcCode = ReferenceCode.AlloyDenied;

        if(this.alloyMode === AlloyMode.EVAL && outcome === AlloyOutcome.Denied && isQualifileDeclined) rcCode = ReferenceCode.AlloyQualifileDecline;

        let stepStateEnum : OnboardingStateEnum = OnboardingStateEnum.Success;
        if(stepState === StateEnum.Fail) stepStateEnum = OnboardingStateEnum.Fail;

        const clonedLogs = Object.assign([], logs);
        const clonedLogDetails = Object.assign([], applicationLogDetailsRequests);

        applicationRequest.step = this.utilitiesService.createApplicantStep(this.step, applicantRequest.applicantId!, stepState, clonedLogs);

        //Capture correct RC Code whenever there is a failure.
        if (stepState === StateEnum.Fail && !rcCode) {
          rcCode = this.alloyMode === AlloyMode.DOCV ? ReferenceCode.DocVDenied : isQualifileDeclined ? ReferenceCode.AlloyQualifileDecline : ReferenceCode.AlloyDenied;
        }

        if(appState === StatusEnum.UnderReview && this.alloyMode == AlloyMode.DOCV) {
          rcCode = ReferenceCode.AlloyUnderReview;
        }

        applicationRequest.applicationLogStepRequest = this.utilitiesService.createEntireApplicationLogStepRequest(this.onboardingStepEnum, stepStateEnum, OnboardingSourceEnum.Bb8, applicantId!, rcCode, clonedLogDetails);

        if (rcCode) applicationRequest.rcCode = rcCode;

        applicationRequest.status = appState;

        await this._updateApplication(applicationRequest);
        //  Fix to address the concurrent log update to db(lost update problem).
        //  sleep thread for 1000ms
        setTimeout(() => {
          if(appState === StatusEnum.UnderReview) {
            if(this.alloyMode == AlloyMode.DOCV) {
              let applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
              applicationRequest.rcCode = ReferenceCode.DocVUnderReview!.toString();
              applicationRequest.applicationLogStepRequest = this.utilitiesService.createEntireApplicationLogStepRequest(this.onboardingStepEnum, stepStateEnum, OnboardingSourceEnum.Bb8, applicantId!, ReferenceCode.DocVUnderReview, []);
              this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
              this._handleAlloyUnderReview(ReferenceCode.DocVUnderReview);
            } else this.processService.triggerAlloyEvaluateProcessComplete();
          } else {
            if (stepState === StateEnum.Success || (this.alloyMode === AlloyMode.EVAL && outcome === AlloyOutcome.ManualReview)) {
              if(this.step === NameEnum.AlloyKyc) this.processService.triggerAlloyEvaluateProcessComplete();
              else this.processService.triggerAlloyDocumentVerifyProcessComplete();
              index++;
              if (this.applicantRequests[index] !== undefined && this.applicantRequests[index] !== null) {
                this.executeAlloyCall(this.applicantRequests[index], index);
              }
            } else if (stepState === StateEnum.Fail) this._handleAlloyFailure(this.alloyMode === AlloyMode.DOCV ? ReferenceCode.DocVDenied: isQualifileDeclined ? ReferenceCode.AlloyQualifileDecline : ReferenceCode.AlloyDenied);
          }
        }, 1000);
      }
    }, async error => {
      let logs: Log[] = [];
      let applicationLogDetails: ApplicationLogDetailsRequest[] = [];

      logs.push(this.utilitiesService.createLog("SKH", JSON.stringify(error)));
      logs.push(this.utilitiesService.createLog("BB8", `Alloy ${this.alloyMode} failed.`));

      //Capture RC Code for any error.
      //IF Alloy Evaluate fails - RC350
      //IF DocV fails - RC351
      applicationRequest.rcCode = this.alloyMode === AlloyMode.EVAL? ReferenceCode.AlloyFailure: ReferenceCode.DocVFailure

      applicationLogDetails.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Skyhopper, JSON.stringify(error)));
      applicationLogDetails.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Alloy ${this.alloyMode} failed.`));

      applicationRequest.step = this.utilitiesService.createApplicantStep(this.step, applicantRequest.applicantId!, StateEnum.Fail, logs);
      applicationRequest.applicationLogStepRequest = this.utilitiesService.createEntireApplicationLogStepRequest(this.onboardingStepEnum, OnboardingStateEnum.Fail, OnboardingSourceEnum.Bb8, applicantRequest.applicantId!, applicationRequest.rcCode, applicationLogDetails);

      if(this.alloyMode === AlloyMode.DOCV && !this.alloyRetryAttempted) {
        logs.push(this.utilitiesService.createLog("BB8", `Alloy ${this.alloyMode} is being reattempted.`));
        applicationLogDetails.push(this.utilitiesService.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Alloy ${this.alloyMode} is being reattempted.`));

        await this._updateApplication(applicationRequest);

        this.executeAlloyCall(this.applicantRequests[index], index);
        this.alloyRetryAttempted = true;
      } else {
        this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
        this._handleAlloyFailure(this.alloyMode === AlloyMode.EVAL? ReferenceCode.AlloyFailure: ReferenceCode.DocVFailure);
      }
    });
  }

  private _updateApplication(applicationRequest: ApplicationLogsRequest): Promise<void> {
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    return this.facadeService.updateApplication();
  }

  private _handleAlloyFailure(refCode: ReferenceCode) {
    this.facadeService.processOnboardingError(refCode).subscribe(() => {
      this.coreService.navigate(`/${ERROR_PATH}`);
    });
  }

  private _handleAlloyUnderReview(refCode: ReferenceCode) {
    this.facadeService.processOnboardingUnderReview(refCode).subscribe(() => {
      this.coreService.navigate(`/${ERROR_PATH}`);
    });
  }
}
